// Unused-entry check (10/2026): the remaining unused keys in `theme.extend`
// (font sizes, spacing steps, rounded-xl) override Tailwind defaults of the same
// name - removing them would bring the defaults back, not remove the class.

const brand = {
  aubergine: "#311123",
  "aubergine-80": "rgba(49, 17, 35, 0.8)",
  "aubergine-64": "rgba(49, 17, 35, 0.64)",
  "aubergine-48": "rgba(49, 17, 35, 0.48)",
  "aubergine-32": "rgba(49, 17, 35, 0.32)",
  "aubergine-16": "rgba(49, 17, 35, 0.16)",
  "aubergine-8": "rgba(49, 17, 35, 0.08)",
  "aubergine-4": "rgba(49, 17, 35, 0.04)",
};

const neutral = {
  white: "#FFFFFF",
  "white-80": "rgba(255, 255, 255, 0.8)",
  "white-64": "rgba(255, 255, 255, 0.64)",
  "white-48": "rgba(255, 255, 255, 0.48)",
  "white-32": "rgba(255, 255, 255, 0.32)",
  "white-16": "rgba(255, 255, 255, 0.16)",
  "white-8": "rgba(255, 255, 255, 0.08)",
  "white-4": "rgba(255, 255, 255, 0.04)",
};

const accent = {
  blue: "#0827A0",
  mint: "#D0F5DA",
  orange: "#FF6047",
  rose: "#FDD2CC",
  ivory: "#F9F5F0",
  beige: "#CCC1B4",
};

const feedback = {
  cobalt: "#2A47BE",
  azure: "#6F9FFF",
  green: "#235831",
  lime: "#76EB95",
  red: "#A61700",
  cinnabar: "#FF6366",
  mustard: "#8E6600",
  canary: "#F9DE74",
};

const fontSize = {
  xs: "12px",
  sm: "14px",
  base: "16px",
  lg: "18px",
  xl: "20px",
  "2xl": "24px",
  "3xl": "28px",
  "4xl": "32px",
  "5xl": "40px",
  "6xl": "48px",
  "7xl": "56px",
  "8xl": "64px",
  "9xl": "72px",
  "10xl": "80px",
  "11xl": "88px",
  "12xl": "96px",
  "13xl": "104px",
  "14xl": "112px",
  "15xl": "120px",
};

const spacing = {
  0: "0px",
  0.25: "1px",
  0.5: "2px",
  0.75: "3px",
  1: "4px",
  1.5: "6px",
  2: "8px",
  2.5: "10px",
  3: "12px",
  3.5: "14px",
  4: "16px",
  5: "20px",
  6: "24px",
  7: "28px",
  7.5: "30px",
  8: "32px",
  9: "36px",
  9.5: "38px",
  10: "40px",
  11: "44px",
  12: "48px",
  13: "56px",
  14: "64px",
  15: "72px",
  16: "80px",
  17: "96px",
  18: "112px",
  19: "128px",
  20: "144px",
  21: "160px",
  22: "176px",
  23: "192px",
};

// Exported so runtime code (src/styles/colors.ts) reads the same color tokens,
// e.g. for libraries and inline styles that need a color value, not a class.
export const foreground = {
  primary: brand.aubergine,
  secondary: brand["aubergine-80"],
  tertiary: brand["aubergine-64"],
  "primary-inverse": neutral.white,
  "secondary-inverse": neutral["white-80"],
  "tertiary-inverse": neutral["white-64"],
  info: feedback.cobalt,
  success: feedback.green,
  error: feedback.red,
  warning: feedback.mustard,
};

export const surface = {
  primary: brand.aubergine,
  secondary: brand["aubergine-16"],
  tertiary: brand["aubergine-8"],
  quaternary: brand["aubergine-4"],
  "primary-inverse": neutral.white,
  "secondary-inverse": neutral["white-16"],
  "tertiary-inverse": neutral["white-8"],
  "quaternary-inverse": neutral["white-4"],
  "accent-blue": accent.blue,
  "accent-mint": accent.mint,
  "accent-orange": accent.orange,
  "accent-rose": accent.rose,
  "accent-beige": accent.beige,
  "accent-ivory": accent.ivory,
  info: feedback.cobalt,
  "info-inverse": feedback.azure,
  success: feedback.green,
  "success-inverse": feedback.lime,
  error: feedback.red,
  "error-inverse": feedback.cinnabar,
  warning: feedback.mustard,
  "warning-inverse": feedback.canary,
};

// Exported so runtime code (src/styles/screens.ts) reads the same breakpoints.
export const screens = {
  tablet: "768px",
  desktop: "1024px",
  desktopLg: "1440px",
};

/** @type {import('tailwindcss').Config} */
export default {
  content: ["./index.html", "./src/**/*.{js,jsx,ts,tsx}"],
  plugins: [
    // Focus that arrived by Tab (`html.user-is-tabbing`, see keyboardModality.ts).
    ({ addVariant }) => {
      addVariant("keyboard-focus", "html.user-is-tabbing &:focus");
      addVariant("group-keyboard-focus", "html.user-is-tabbing .group:focus &");
    },
  ],
  theme: {
    // Not in `extend`: this replaces Tailwind's default sm/md/lg/xl/2xl, so
    // only the Figma design-system tiers exist (mobile 360–767px is the
    // unprefixed base).
    screens,
    extend: {
      fontFamily: {
        primary: ["Gellix", "sans-serif"],
      },
      fontWeight: {
        regular: "400",
        semibold: "600",
      },
      fontSize: {
        ...fontSize,
        // Letter-spacing is part of each Figma text style (docs/design-system/typography.md),
        // so elements never need their own tracking-* class.
        "headline-sm": [fontSize.xl, { letterSpacing: "-0.01em" }],
        // headline-md/lg/xl/2xl/3xl grow at the tablet/desktop/desktopLg breakpoints — see src/styles/tokens.scss
        "headline-md": [
          "var(--font-size-headline-md)",
          { letterSpacing: "-0.01em" },
        ],
        "headline-lg": [
          "var(--font-size-headline-lg)",
          { letterSpacing: "-0.01em" },
        ],
        "headline-xl": [
          "var(--font-size-headline-xl)",
          { letterSpacing: "-0.02em" },
        ],
        "headline-2xl": [
          "var(--font-size-headline-2xl)",
          { letterSpacing: "-0.02em" },
        ],
        "headline-3xl": [
          "var(--font-size-headline-3xl)",
          { letterSpacing: "-0.02em" },
        ],
        "body-xs": [fontSize.xs, { letterSpacing: "0.02em" }],
        "body-sm": [fontSize.sm, { letterSpacing: "0.02em" }],
        "body-base": [fontSize.base, { letterSpacing: "0.02em" }],
        "body-lg": [fontSize.lg, { letterSpacing: "0.01em" }],
      },
      lineHeight: {
        none: "1",
        xs: "1.1",
        sm: "1.2",
        md: "1.3",
      },
      spacing: {
        ...spacing,
        // gutter/section-* grow at the tablet/desktop/desktopLg breakpoints — see src/styles/tokens.scss
        gutter: "var(--spacing-gutter)",
        "section-padding-horizontal":
          "var(--spacing-section-padding-horizontal)",
        "section-padding-vertical": "var(--spacing-section-padding-vertical)",
        "section-gap": "var(--spacing-section-gap)",
      },
      // column count grows at the tablet/desktop breakpoints — see src/styles/tokens.scss and .grid-container in src/styles/grid.scss
      gridTemplateColumns: {
        grid: "repeat(var(--grid-columns), minmax(0, 1fr))",
        16: "repeat(16, minmax(0, 1fr))",
      },
      transitionProperty: {
        height: "height",
        // Button background layer: shrinks on hover/press (tertiary also fades in)
        inset: "inset",
        "inset-colors": "inset, background-color",
      },
      transitionDuration: {
        120: "120ms",
      },
      transitionTimingFunction: {
        // CSS `ease` - Tailwind's own `ease-*` presets are all cubic-beziers
        standard: "ease",
      },
      borderWidth: {
        1: "1px",
        none: "0px",
        md: "2px",
      },
      borderRadius: {
        none: "0px",
        sm: "2px",
        md: "4px",
        lg: "8px",
        xl: "12px",
        full: "9999px",
      },
      // Only the semantic tokens become utilities (AGENTS.md rule 3); the raw
      // brand/neutral/accent/feedback palettes above are just their source values.
      colors: {
        foreground,
        surface,
        background: {
          primary: neutral.white,
          secondary: accent.ivory,
        },
        border: {
          primary: brand.aubergine,
          secondary: brand["aubergine-64"],
          tertiary: brand["aubergine-16"],
          "primary-inverse": neutral.white,
          "secondary-inverse": neutral["white-64"],
          "tertiary-inverse": neutral["white-16"],
          error: feedback.red,
          "error-inverse": feedback.cinnabar,
        },
      },
      important: true,
      keyframes: {
        slideDown: {
          "0%": { transform: "translateY(-100%)", opacity: "0" },
          "100%": { transform: "translateY(0)", opacity: "1" },
        },
      },
      animation: {
        slideDown: "slideDown 0.3s ease-out",
      },
    },
  },
};
